import React, { useRef, useEffect, useState } from 'react';
import { Button } from '@rocco/components/button';
import { ReadMore } from '@rocco/components/read-more';
import type { ProjectPageLoader } from '../../type/loader';
import {
    ACHIPRO_PHOTOGRAPHER_SPLIT,
    getDetailsItems,
} from '../details/util/get-details-items';
import { useFeature } from '~/modules/root';
import { useTracker } from '@archipro-website/tracker';
import { useEnquiryModal } from '~/modules-rocco/enquiry/hooks/use-enquiry-modal';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';
import { ReactComponent as LinkIcon } from '@rocco/icons/svg/arrow/arrow-offsite.svg';
import { ReactComponent as ArrowChevronDown } from '@rocco/icons/svg/arrow/arrow-chevron-down-bold.svg';
import ProjectDescriptionContent from './ProjectDescriptionContent';
/**
 * Interface for the project showcase card props
 */
interface ProjectShowcaseCardProps {
    /** Array of project details to display */
    details: ProjectPageLoader['project'];
}

/**
 * Sanitizes HTML content to prevent XSS attacks
 * Uses regex-based string manipulation to work consistently on both server and client
 * @param html - The HTML string to sanitize
 * @returns Sanitized HTML string
 */
const sanitizeHtml = (html: string): string => {
    if (!html) return '';

    let sanitized = html;

    // Remove dangerous tags completely (including content)
    const dangerousTags = [
        'script',
        'style',
        'iframe',
        'object',
        'embed',
        'link',
        'meta',
    ];

    dangerousTags.forEach((tag) => {
        const regex = new RegExp(`<${tag}[^>]*>.*?</${tag}>`, 'gis');
        sanitized = sanitized.replace(regex, '');
        // Also remove self-closing tags
        const selfClosingRegex = new RegExp(`<${tag}[^>]*/?>`, 'gi');
        sanitized = sanitized.replace(selfClosingRegex, '');
    });

    // Keep safe formatting tags - these are essential for WYSIWYG content display
    // Only remove truly dangerous formatting that could be used for XSS
    const dangerousFormattingTags = [
        'ins', // inserted text - could be used maliciously
    ];

    dangerousFormattingTags.forEach((tag) => {
        // Replace opening and closing tags with empty string, preserving content
        const openingRegex = new RegExp(`<${tag}[^>]*>`, 'gi');
        const closingRegex = new RegExp(`</${tag}>`, 'gi');
        sanitized = sanitized
            .replace(openingRegex, '')
            .replace(closingRegex, '');
    });

    // Remove dangerous attributes from all tags
    const dangerousAttributes = [
        'onerror',
        'onload',
        'onclick',
        'onmouseover',
        'onfocus',
        'oninput',
        'onchange',
        'onsubmit',
    ];

    dangerousAttributes.forEach((attr) => {
        const attrRegex = new RegExp(`\\s${attr}\\s*=\\s*["'][^"']*["']`, 'gi');
        sanitized = sanitized.replace(attrRegex, '');
    });

    // Clean up multiple spaces and trim
    sanitized = sanitized.replace(/\s+/g, ' ').trim();

    return sanitized;
};

/**
 * Truncates text to a specified length and adds "Read more" link
 * @param text - The text to truncate
 * @param maxLength - Maximum length before truncation
 * @returns Truncated text with optional "Read more" linkå
 */
const truncateText = (
    text: string,
    maxLength?: number
): { content: string; isTruncated: boolean } => {
    if (!maxLength || text.length <= maxLength) {
        return { content: text, isTruncated: false };
    }

    const truncated = text.substring(0, maxLength);
    const lastSpaceIndex = truncated.lastIndexOf(' ');
    const finalText =
        lastSpaceIndex > 0 ? truncated.substring(0, lastSpaceIndex) : truncated;

    return {
        content: `${finalText}...`,
        isTruncated: true,
    };
};

/**
 * Modern project showcase card component with horizontal layout
 *
 * Features:
 * - Two-column responsive layout (40% details, 60% description)
 * - Dynamic project details display
 * - HTML/markdown content support with sanitization
 * - Call-to-action section
 * - Responsive design that stacks on mobile
 */
export const ProjectShowcaseCard: React.FC<ProjectShowcaseCardProps> = ({
    details,
}) => {
    const description = details.Content || '';
    const sanitizedDescription = sanitizeHtml(description);
    const { content: displayContent } = truncateText(
        sanitizedDescription,
        9999
    );
    const hasProjectAddressFeature = useFeature('feature_project_address');

    const { openEnquiryModal } = useEnquiryModal({
        professional: details.Professional,
        itemType: 'professional',
        siteTreeID: details.ID,
        relatedItem: {
            id: details.ID,
            type: 'project',
            title: details.Title,
            category: details.categoryPath,
        },
    });
    const { handleFollow, isInDesignBoard } = useHandleFollow();
    const isShortlisted = isInDesignBoard(details.ID, 'ProjectPin');
    const tracker = useTracker();

    const detailsItems = getDetailsItems(details, {
        includeAddress: hasProjectAddressFeature,
        includeDownloads: false,
        includeNewFields: true,
    }).filter((item) => item.itemName && item.itemName !== 'Awards');

    const componentRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const [shouldShowButton, setShouldShowButton] = useState(false);

    // Detect if content overflows the 10-line limit
    useEffect(() => {
        const checkOverflow = () => {
            if (!contentRef.current) return;

            const element = contentRef.current;
            // Check if scrollHeight exceeds clientHeight, indicating overflow
            const hasOverflow = element.scrollHeight > element.clientHeight;
            setShouldShowButton(hasOverflow);
        };

        // Small delay to ensure content is rendered
        const timer = setTimeout(checkOverflow, 100);

        // Also check on window resize
        window.addEventListener('resize', checkOverflow);
        return () => {
            clearTimeout(timer);
            window.removeEventListener('resize', checkOverflow);
        };
    }, [displayContent]);

    return (
        <div
            ref={componentRef}
            className="flex scroll-mt-29 gap-81 pb-17 pt-35"
        >
            <div className="relative -top-2 col-span-2">
                <dl className="ml-12 flex w-110 flex-col font-display">
                    {detailsItems.map((detail, index) => (
                        <div
                            key={`${detail.itemName}-${index}`}
                            className="flex-start flex items-start justify-between border-b border-[#323232]/30 py-1.5"
                        >
                            <dt className="w-44 px-1 text-4 font-[600] leading-[1.2] text-[#333333]">
                                {detail.itemNameDesktop || detail.itemName}:
                            </dt>
                            <span className="flex items-center justify-end font-[600]">
                                {detail.itemNameDesktop === 'Category' ||
                                detail.itemName === 'Category' ? (
                                    detail.value.split(',').length > 0 &&
                                    detail.value.split(',')[0] !== '' &&
                                    detail.value
                                        .split(',')
                                        .slice(0, 2)
                                        .map((value, index) => (
                                            <dd
                                                key={`category-${index}`}
                                                className="flex min-w-0 items-center text-right text-4 leading-[1.2] text-[#333333]"
                                            >
                                                <a
                                                    href={
                                                        detail.link?.split(',')[
                                                            index
                                                        ]
                                                    }
                                                    className="flex min-w-0 items-center justify-end gap-2 hover:opacity-65"
                                                >
                                                    <span className="max-w-65 overflow-hidden text-ellipsis whitespace-nowrap">
                                                        {value}
                                                    </span>
                                                    {index ===
                                                        detail.value
                                                            .split(',')
                                                            .slice(0, 2)
                                                            .length -
                                                            1 && (
                                                        <LinkIcon className="h-3 w-3 text-[#313131]" />
                                                    )}
                                                </a>
                                                {index !==
                                                    detail.value
                                                        .split(',')
                                                        .slice(0, 2).length -
                                                        1 && (
                                                    <span className="ml-2.5 mr-1.5">
                                                        /
                                                    </span>
                                                )}
                                            </dd>
                                        ))
                                ) : detail.itemName === 'Photographers' ? (
                                    <dd className="text-right text-4 leading-[1.2] text-[#333333]">
                                        {detail.value
                                            .split(ACHIPRO_PHOTOGRAPHER_SPLIT)
                                            .map((s) => s.trim())
                                            .filter(Boolean)
                                            .map((value, index) => (
                                                <span
                                                    key={`photog-${index}`}
                                                    className="block"
                                                >
                                                    {detail.link?.split(
                                                        ACHIPRO_PHOTOGRAPHER_SPLIT
                                                    )[index] ? (
                                                        <a
                                                            href={
                                                                detail.link?.split(
                                                                    ACHIPRO_PHOTOGRAPHER_SPLIT
                                                                )[index]
                                                            }
                                                            className="flex items-center justify-end gap-2 hover:opacity-65"
                                                        >
                                                            <span className="max-w-65 overflow-hidden text-ellipsis whitespace-nowrap">
                                                                {value}
                                                            </span>
                                                            <LinkIcon className="h-3 w-3 text-[#313131]" />
                                                        </a>
                                                    ) : (
                                                        <div className="flex items-center justify-end gap-2">
                                                            <span>{value}</span>
                                                            <LinkIcon className="h-3 w-3 text-[#313131] opacity-0" />
                                                        </div>
                                                    )}
                                                </span>
                                            ))}
                                    </dd>
                                ) : (
                                    <dd className="text-right text-4 leading-[1.2] text-[#333333]">
                                        {detail.link ? (
                                            <a
                                                href={detail.link}
                                                className="flex items-center justify-end gap-2 hover:opacity-65"
                                            >
                                                <span>{detail.value}</span>
                                                <LinkIcon className="h-3 w-3 text-[#313131]" />
                                            </a>
                                        ) : (
                                            <div className="flex items-center justify-end gap-2">
                                                <span>{detail.value}</span>
                                                <LinkIcon className="h-3 w-3 text-[#313131] opacity-0" />
                                            </div>
                                        )}
                                    </dd>
                                )}
                            </span>
                        </div>
                    ))}
                </dl>
            </div>

            <div className="relative -top-[2px] w-242">
                {displayContent.length > 0 && (
                    <ReadMore
                        maxLines={10}
                        buttonClassName="md:text-5.5 text-black font-semibold md:mt-[2.5rem]"
                        readMoreText={
                            <div className="flex items-center gap-2 font-display">
                                <span>Read more</span>
                                <ArrowChevronDown className="relative top-0.5 w-4 text-black" />
                            </div>
                        }
                        readLessText={
                            <div className="flex items-center gap-2 font-display">
                                <span>Read less</span>
                                <ArrowChevronDown className="relative top-[3px] w-4 rotate-180 text-black" />
                            </div>
                        }
                        buttonRequired
                        alwaysShowButton={shouldShowButton}
                        onToggle={(isClamped) => {
                            if (!isClamped) {
                                // Track the click event when expanding
                                tracker.log('ProjectDetailsExpandClick', {
                                    url: new URL(window.location.href),
                                    targetTracker: 'archiproTracker',
                                });
                            } else {
                                window.scrollTo({
                                    top: componentRef.current?.offsetTop || 0,
                                    behavior: 'smooth',
                                });
                            }
                        }}
                    >
                        <div
                            ref={contentRef}
                            className="whitespace-pre-wrap font-display text-5.5 font-normal leading-[130%] -tracking-[1%] text-[#161616]"
                        >
                            <ProjectDescriptionContent
                                content={displayContent}
                            />
                        </div>
                    </ReadMore>
                )}

                {/* Move CTA buttons below the Read more in the right column */}
                <div className="pt-20">
                    <div className="flex w-77 flex-col gap-[6px]">
                        <Button
                            onClick={() =>
                                handleFollow(
                                    {
                                        id: details.ID,
                                        libraryItemId: details.ID,
                                    },
                                    'ProjectPin'
                                )
                            }
                            color="dark"
                            className="w-77 rounded-full py-3"
                        >
                            {isShortlisted ? 'Saved' : 'Save this Project'}
                        </Button>
                        <Button
                            onClick={() => openEnquiryModal()}
                            color="outline"
                            size="larger"
                            className="w-77 rounded-full py-3"
                        >
                            Request Quote
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};
